import AutocompleteDetails from '../../domain/models/AutocompleteDetails'
import AutocompleteSuggestion from '../../domain/models/AutocompleteSuggestion'
import { IMultisearchProvider } from '../../domain/providers/MultisearchProvider'
import WoosmapMultisearch from './MultisearchTypes'
import { MAP_PLACES } from './places'
import { GOOGLE_PLACES_API_KEY } from '../../application/config'

class MultisearchProvider implements IMultisearchProvider {
  private multisearch: WoosmapMultisearch.MultiSearchObject | null

  constructor() {
    this.multisearch = null
  }

  public init(woosmapPublicKey: string, language: string = 'en') {
    this.multisearch = window.woosmap.multisearch({
      apiOrder: ['places', 'address', 'localities', 'store'],
      localities: {
        key: woosmapPublicKey,
        params: {
          language,
          types: MAP_PLACES.join('|')
        },
        fallbackBreakpoint: 0.4
      },
      store: {
        key: woosmapPublicKey,
        params: {
          language
        },
        fallbackBreakpoint: false
      },
      address: {
        key: woosmapPublicKey,
        params: {
          language,
          types: 'house_number|route|address_block|locality|admin_level|country'
        },
        fallbackBreakpoint: 0.5
      },
      places: {
        key: GOOGLE_PLACES_API_KEY,
        params: {
          language
        },
        fallbackBreakpoint: 0.5
      }
    })
  }

  public async autocompleteMulti(input: string): Promise<Array<AutocompleteSuggestion>> {
    if (!this.multisearch) {
      throw Error('Multisearch not ready')
    }
    const res: WoosmapMultisearch.AutocompleteResponseItem[] = await this.multisearch.autocompleteMulti(input, {
      debounceTime: 500
    })
    return res.map((response) => AutocompleteSuggestion.from(response))
  }

  public async detailsMulti(input: {
    id: string
    api: 'localities' | 'address' | 'store' | 'places'
  }): Promise<AutocompleteDetails | null> {
    if (!this.multisearch) {
      return null
    }
    const res = await this.multisearch.detailsMulti(input)
    return AutocompleteDetails.from(res)
  }

  public async detailsAddress(id: string): Promise<AutocompleteDetails | null> {
    if (!this.multisearch) {
      throw Error('Multisearch not ready')
    }

    const res = await this.multisearch.detailsAddress(id)
    return AutocompleteDetails.from(res)
  }

  public async autocompleteStore(input: string): Promise<AutocompleteSuggestion[]> {
    if (!this.multisearch) {
      throw Error('Multisearch not ready')
    }
    const res = await this.multisearch.autocompleteStore(input)
    return res.map((response) => AutocompleteSuggestion.from(response))
  }

  public async autocompleteAddress(input: string): Promise<AutocompleteSuggestion[]> {
    if (!this.multisearch) {
      throw Error('Multisearch not ready')
    }
    const res = await this.multisearch.autocompleteAddress(input)
    return res.map((response) => AutocompleteSuggestion.from(response))
  }
}

export default MultisearchProvider
